import type * as Monaco from "monaco-editor";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { lintSource, previewDerived, useCatalog, useDeleteFile, useDerived, useFlights, useProjectFile, useProjectFiles, useSaveFile } from "../../api/hooks";
import type { DerivedPreview, Problem } from "../../api/types";
import { useProblems } from "../../app/problems";
import { usePendingOpenFile, usePublishActiveFile } from "../../app/activeFile";
import { useSession } from "../../app/store";
import { Sparkline } from "../../components/Sparkline";
import { Split } from "../../components/Split";
import { Empty, Modal, Spinner, errText } from "../../components/ui";
import { CodeEditor } from "../../editor/CodeEditor";
import "./derived.css";

const NEW_DERIVED = (name: string) => `from reports_studio.derived import derived
import numpy as np


@derived(name="${name.toUpperCase()}", units="", description="What this parameter means")
def ${name}(ctx):
    """Vectorised: return one value per second (or a (times, values) tuple at native rate).

    ctx.p("ALT")            1 Hz numpy array of a frame parameter
    ctx.series("ACCvert")   native-rate ParameterSeries (.times, .values, .rate)
    ctx.derived("OTHER")    another derived parameter (resolved on demand)
    ctx.t                   seconds since start (numpy array)
    ctx.inputs / ctx.flight project inputs and flight metadata
    """
    alt = ctx.p("ALT")
    return np.gradient(alt) * 60.0  # ft/min
`;

export function DerivedPage() {
  const { projectId, activeFlight, toast } = useSession();
  const files = useProjectFiles(projectId, "derived");
  const derived = useDerived(projectId);
  const catalog = useCatalog(projectId);
  const flights = useFlights();
  const save = useSaveFile(projectId);
  const del = useDeleteFile(projectId);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);
  const file = useProjectFile(projectId, selected);
  const [draft, setDraft] = useState<string | null>(null);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [preview, setPreview] = useState<DerivedPreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [livePreview, setLivePreview] = useState(true);
  const [creating, setCreating] = useState(false);
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const setDockProblems = useProblems((s) => s.setProblems);
  const setJump = useProblems((s) => s.setJump);

  useEffect(() => {
    if (!files.data || files.isFetching) return;
    if (selected && files.data.some((f) => f.path === selected)) return;
    setSelected(files.data[0]?.path ?? null);
  }, [files.data, files.isFetching, selected]);
  useEffect(() => {
    setDraft(null);
    setPreview(null);
    setPreviewErr(null);
  }, [selected, file.data?.modified]);
  usePublishActiveFile(selected);
  usePendingOpenFile("derived/", setSelected);

  const source = draft ?? file.data?.content ?? "";
  const dirty = draft !== null && draft !== (file.data?.content ?? "");

  useEffect(() => {
    if (!projectId || !selected || !file.data) return;
    const h = setTimeout(() => {
      lintSource(projectId, selected, source)
        .then((r) => {
          setProblems(r.problems);
          setDockProblems(selected, r.problems);
        })
        .catch(() => undefined);
    }, 500);
    return () => clearTimeout(h);
  }, [projectId, selected, source, file.data, setDockProblems]);

  useEffect(() => {
    setJump((f, line, col) => {
      if (f !== selected) setSelected(f);
      setTimeout(() => {
        const ed = editorRef.current;
        if (!ed) return;
        ed.revealLineInCenter(line);
        ed.setPosition({ lineNumber: line, column: col + 1 });
        ed.focus();
      }, f !== selected ? 300 : 0);
    });
    return () => setJump(null);
  }, [selected, setJump]);

  const runPreview = useCallback(async () => {
    if (!projectId || !selected || !activeFlight) return;
    setPreviewing(true);
    setPreviewErr(null);
    try {
      setPreview(await previewDerived(projectId, { flight_id: activeFlight, path: selected, source, points: 300 }));
    } catch (e) {
      setPreviewErr(errText(e));
    } finally {
      setPreviewing(false);
    }
  }, [projectId, selected, activeFlight, source]);

  // live preview (debounced) when the source changes and there are no lint errors
  useEffect(() => {
    if (!livePreview || !activeFlight || !file.data) return;
    if (problems.some((p) => p.severity === "error")) return;
    const h = setTimeout(() => void runPreview(), 900);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, activeFlight, livePreview, file.data, problems]);

  const doSave = useCallback(() => {
    if (!selected || draft === null) return;
    save.mutate(
      { path: selected, content: draft },
      {
        onSuccess: (r) => {
          toast("Saved", "success");
          if (r.problems) {
            setProblems(r.problems);
            setDockProblems(selected, r.problems);
          }
        },
        onError: (e) => toast(errText(e), "error"),
      },
    );
  }, [selected, draft, save, toast, setDockProblems]);

  const flightLabel = useMemo(() => {
    const f = flights.data?.find((x) => x.id === activeFlight);
    return f ? `${f.tail ?? "?"} ${f.date?.slice(0, 10) ?? ""}` : "no active flight";
  }, [flights.data, activeFlight]);

  const frameMnemonics = useMemo(() => new Set(catalog.data?.parameters.map((p) => p.mnemonic) ?? []), [catalog.data]);
  const derivedNames = useMemo(() => new Set((derived.data?.parameters ?? []).map((p) => p.name)), [derived.data]);
  const inThisFile = useMemo(() => (derived.data?.parameters ?? []).filter((p) => p.file === selected), [derived.data, selected]);
  const shown = preview?.parameters?.length ? preview.parameters : inThisFile;
  const markers = useMemo(() => {
    const m: Problem[] = [...problems];
    for (const e of preview?.errors ?? []) {
      if (e.line && (!e.file || e.file.endsWith(selected?.split("/").pop() ?? "")))
        m.push({ severity: "error", line: e.line, col: 0, message: `${e.name}: ${e.error}`, code: "derived" });
    }
    return m;
  }, [problems, preview, selected]);

  if (!projectId) return <Empty title="No project selected" />;

  return (
    <div className="page derived-page">
      <div className="page-head">
        <h1>Derived parameters</h1>
        <span className="sub">Vectorised Python that adds parameters to the frame; triggers and reports use them like any mnemonic</span>
        <span className="spacer" />
        <button className="btn" onClick={() => setCreating(true)}>
          ＋ New file
        </button>
        <button className="btn primary" disabled={!dirty || save.isPending} onClick={doSave} title="Ctrl+S">
          Save
        </button>
      </div>
      <div className="page-body">
        <Split id="derived" sizes={[200, "1fr", 380]} minSizes={[140, 320, 220]}>
          <div className="pane">
            <div className="pane-head">Files</div>
            <div className="list">
              {files.data?.map((f) => {
                const n = (derived.data?.parameters ?? []).filter((p) => p.file === f.path).length;
                const bad = derived.data?.errors.some((e) => e.file === f.path);
                return (
                  <div key={f.path} className={`list-item${f.path === selected ? " sel" : ""}`} onClick={() => setSelected(f.path)}>
                    <span className="name mono">{f.name}</span>
                    {bad ? <span className="badge err">error</span> : <span className="badge">{n}</span>}
                  </div>
                );
              })}
              {files.data && !files.data.length ? <div className="empty small">No derived files yet.</div> : null}
            </div>
            {derived.data?.parameters.length ? (
              <>
                <div className="pane-head">All derived</div>
                <div className="list small">
                  {derived.data.parameters.map((p) => (
                    <div key={p.name} className="list-item" onClick={() => p.file && setSelected(p.file)} title={p.description ?? ""}>
                      <span className="name mono">{p.name}</span>
                      <span className="faint">{p.units ?? ""}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          <div className="pane" style={{ overflow: "hidden" }}>
            {selected && file.data ? (
              <>
                <div className="pane-head">
                  <span className="mono">{selected}</span>
                  {dirty ? <span className="badge warn">modified</span> : null}
                  <span className="spacer" />
                  <label className="row small dim" style={{ gap: 4 }}>
                    <input type="checkbox" checked={livePreview} onChange={(e) => setLivePreview(e.target.checked)} /> live preview
                  </label>
                  <button className="btn sm primary" disabled={!activeFlight || previewing} onClick={() => void runPreview()} title="Evaluate the editor text against the active flight">
                    ▶ Preview on {flightLabel}
                  </button>
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <CodeEditor key={selected} path={selected} value={source} onChange={setDraft} onSave={doSave} markers={markers} onMount={(ed) => (editorRef.current = ed)} />
                </div>
              </>
            ) : (
              <Empty title="Select a derived file">
                Decorate a function with <code>@derived(name=…)</code>; it receives a context with <code>ctx.p()</code>, <code>ctx.series()</code>,{" "}
                <code>ctx.derived()</code>, <code>ctx.t</code>, <code>ctx.inputs</code> and <code>ctx.flight</code>, and returns one value per second
                (or a <code>(times, values)</code> tuple for native-rate output).
              </Empty>
            )}
          </div>

          <div className="pane">
            <div className="pane-head">
              Preview
              {previewing ? <Spinner /> : null}
              <span className="spacer" />
              {preview ? <span className="dim small">{preview.total_seconds}s</span> : null}
            </div>
            <div className="pane-body col">
              {!activeFlight ? <div className="dim small">Pick an active flight on the Flights screen to see values.</div> : null}
              {previewErr ? <div className="err small">{previewErr}</div> : null}
              {shown.map((p) => {
                const tile = preview?.series[p.name];
                const err = preview?.errors.find((e) => e.name === p.name);
                return (
                  <div key={p.name} className={`card derived-card${err ? " bad" : ""}`}>
                    <div className="row">
                      <strong className="mono">{p.name}</strong>
                      {p.units ? <span className="badge">{p.units}</span> : null}
                      {tile ? <span className="badge">{tile.rate} Hz</span> : null}
                      <span className="spacer" />
                      {tile ? (
                        <button
                          className="btn sm ghost"
                          title="Open in Analysis"
                          onClick={() => {
                            const cur = useSession.getState().analysisParams;
                            useSession.getState().setAnalysisParams([...new Set([...cur, p.name])]);
                            navigate("/analysis");
                          }}
                        >
                          📈
                        </button>
                      ) : null}
                    </div>
                    {p.description ? <div className="dim small">{p.description}</div> : null}
                    {tile ? (
                      <>
                        <Sparkline t={tile.t} v={tile.v} totalSeconds={preview?.total_seconds} width={340} height={56} title={p.name} />
                        <div className="row small dim mono">
                          <span>
                            {fmt(tile.min)} … {fmt(tile.max)}
                          </span>
                          <span className="spacer" />
                          <span>{tile.samples} samples</span>
                        </div>
                      </>
                    ) : null}
                    {err ? (
                      <div className="err small">
                        {err.error}
                        {err.traceback ? (
                          <details>
                            <summary>traceback</summary>
                            <pre className="mono small" style={{ whiteSpace: "pre-wrap" }}>
                              {err.traceback}
                            </pre>
                          </details>
                        ) : null}
                      </div>
                    ) : null}
                    {p.depends.length ? (
                      <div className="row wrap" style={{ marginTop: 4 }}>
                        <span className="faint small">reads</span>
                        {p.depends.map((d) => (
                          <span
                            key={d}
                            className={`badge ${derivedNames.has(d) || preview?.series[d] ? "info" : frameMnemonics.has(d) ? "ok" : "err"}`}
                            title={derivedNames.has(d) ? "derived parameter" : frameMnemonics.has(d) ? "frame parameter" : "not in the frame"}
                          >
                            {d}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {preview?.errors
                .filter((e) => !shown.some((p) => p.name === e.name))
                .map((e, i) => (
                  <div key={i} className="card derived-card bad">
                    <strong className="mono">{e.name}</strong>
                    <div className="err small">{e.error}</div>
                    {e.problems?.map((p, j) => (
                      <div key={j} className="err small">
                        line {p.line}: {p.message}
                      </div>
                    ))}
                  </div>
                ))}
              {selected && !shown.length && !preview?.errors.length ? <span className="dim small">No @derived function in this file yet.</span> : null}
              {selected ? (
                <button
                  className="btn sm danger"
                  style={{ alignSelf: "flex-start", marginTop: 8 }}
                  onClick={() => {
                    if (confirm(`Delete ${selected}?`)) del.mutate(selected, { onSuccess: () => setSelected(null), onError: (e) => toast(errText(e), "error") });
                  }}
                >
                  Delete file
                </button>
              ) : null}
            </div>
          </div>
        </Split>
      </div>
      {creating ? (
        <NewDerivedDialog
          onClose={() => setCreating(false)}
          onCreate={(name) =>
            save.mutate(
              { path: `derived/${name}.py`, content: NEW_DERIVED(name) },
              {
                onSuccess: () => {
                  setSelected(`derived/${name}.py`);
                  setCreating(false);
                },
                onError: (e) => toast(errText(e), "error"),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

function fmt(x: number | null | undefined): string {
  if (x == null || !Number.isFinite(x)) return "—";
  return Math.abs(x) >= 1000 ? x.toFixed(0) : x.toFixed(2);
}

function NewDerivedDialog({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = useState("");
  const ok = /^[a-z_][a-z0-9_]*$/.test(name);
  return (
    <Modal
      title="New derived parameter file"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn primary" disabled={!ok} onClick={() => onCreate(name)}>
            Create
          </button>
        </>
      }
    >
      <label className="field">
        <span>File / function name (snake_case)</span>
        <input className="inp" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="vertical_speed" />
      </label>
      <p className="dim small" style={{ margin: 0 }}>
        One file can hold several <code>@derived</code> functions; they may depend on each other through <code>ctx.derived()</code>.
      </p>
    </Modal>
  );
}
